import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CLoadingButton,
  CSpinner,
} from '@coreui/react-pro'
import React, { useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import GamePrizeService from 'src/service/GamePrizeService'
import ToastComponent from 'src/components/common/TaostComponent'
import ImageDropzone from 'src/components/common/ImageDropzone'
import useSponsorQuillModules from '../Sponsor/useSponsorQuillModules'

const SUPPORTED_FORMATS = ['image/jpg', 'image/png', 'image/jpeg', 'image/gif']
const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/gif'
const MAX_IMAGE_SIZE = 1024 * 1024 // 1MB

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required').max(50, '50 Character Limit is allowed.'),
  image: Yup.mixed()
    .nullable()
    .test('size', 'File size too large, max file size is 1MB', (value) =>
      value ? value.size <= MAX_IMAGE_SIZE : true,
    )
    .test(
      'type',
      'Invalid file format selection',
      (value) => !value || SUPPORTED_FORMATS.includes(value?.type),
    ),
})

const EditForm = ({ prizeId, visible, onClose, fetchUsers }) => {
  const [prizeDetail, setPrizeDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [loader, setLoader] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])
  const quillRef = useRef()
  const modules = useSponsorQuillModules(quillRef)

  useEffect(() => {
    if (visible && prizeId) {
      setDetailLoading(true)
      GamePrizeService.getPrizeDetail(prizeId)
        .then((res) => {
          if (res.status === 200) {
            setPrizeDetail(res.data)
          }
        })
        .catch((e) => {
          ToastComponent(e.response?.data?.message || 'Something went wrong.', 'error')
        })
        .finally(() => setDetailLoading(false))
    }
  }, [visible, prizeId])

  const formik = useFormik({
    initialValues: {
      name: prizeDetail?.title || '',
      prize_type: prizeDetail?.prize_type || '',
      about: prizeDetail?.description || '',
      image: null,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data) => {
      const formData = new FormData()
      formData.append('prizeId', prizeId)
      formData.append('name', data.name)
      formData.append('prize_type', data.prize_type)
      formData.append('about', data.about)
      if (data.image) formData.append('image', data.image)

      setLoader(true)
      GamePrizeService.editPrize(formData)
        .then((res) => {
          if (res.status === 200) {
            setErrorMessages([])
            fetchUsers?.()
            ToastComponent(res.message, 'success')
            onClose()
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
        })
        .finally(() => setLoader(false))
    },
  })

  const handleClose = () => {
    setErrorMessages([])
    setPrizeDetail(null)
    formik.resetForm()
    onClose()
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 560 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Edit Prize</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Update {prizeDetail?.title || 'this prize'}&apos;s details, image and description.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        {detailLoading ? (
          <div className="d-flex align-items-center gap-2 text-medium-emphasis py-4">
            <CSpinner size="sm" />
            <span>Loading prize details...</span>
          </div>
        ) : (
          <CForm className="row g-3 flex-grow-1 align-content-start" onSubmit={formik.handleSubmit}>
            <CCol xs={12}>
              <CFormLabel htmlFor="name">Prize Name *</CFormLabel>
              <CFormInput
                id="name"
                name="name"
                placeholder="Prize Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.errors.name && formik.touched.name)}
              />
              {formik.errors.name && formik.touched.name && (
                <CFormFeedback invalid>{formik.errors.name}</CFormFeedback>
              )}
            </CCol>
            <CCol xs={12}>
              <CFormLabel htmlFor="prize_type">Prize Type</CFormLabel>
              <CFormInput
                id="prize_type"
                name="prize_type"
                placeholder="Enter Prize Type"
                value={formik.values.prize_type}
                onChange={formik.handleChange}
              />
            </CCol>
            <CCol xs={12}>
              <CFormLabel htmlFor="image">Prize Image</CFormLabel>
              <ImageDropzone
                accept={ACCEPTED_IMAGE_TYPES}
                hint="JPG, PNG or GIF up to 1MB."
                minHeight={140}
                file={formik.values.image}
                existingUrl={prizeDetail?.image || null}
                error={formik.touched.image ? formik.errors.image : null}
                onChange={(selected) => {
                  formik.setTouched({ ...formik.touched, image: true })
                  formik.setFieldValue('image', selected)
                }}
              />
            </CCol>
            <CCol xs={12}>
              <CFormLabel htmlFor="about">Prize Description</CFormLabel>
              <div className="gp-editor">
                <ReactQuill
                  theme="snow"
                  ref={quillRef}
                  value={formik.values.about}
                  onChange={(content) => formik.setFieldValue('about', content)}
                  modules={modules}
                />
              </div>
            </CCol>
          </CForm>
        )}
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <CLoadingButton color="primary" loading={loader} onClick={() => formik.handleSubmit()}>
          Save Changes
        </CLoadingButton>
      </div>
    </COffcanvas>
  )
}

export default EditForm
